// @ai-generated 2026-07-28T15:01:24+00:00 by niklas.eicker@tngtech.com using Claude Opus 5
import {
    EnumRequisitionOrigin,
    EnumRequisitionStatus,
    getPortalRequisitionStatusLabel,
    isSelfSubmittedRequisition,
} from '@Model';
import { renderDateTime, type StatusTimelineAccent, type StatusTimelineStep } from '@synqo/frontend-component-library';
import type { ReactNode } from 'react';
import { type PortalRequisitionOrderFacts } from './portalRequisitionOrderFacts';

const PENDING_SUBTITLE = 'ausstehend';

/** Listed explicitly so the order does not depend on the generated enum's numeric values. */
const TIMELINE_STAGES: readonly EnumRequisitionStatus[] = [
    EnumRequisitionStatus.Open,
    EnumRequisitionStatus.UnderReview,
    EnumRequisitionStatus.InProgress,
    EnumRequisitionStatus.Completed,
];

const STAGE_ACCENTS: Partial<Record<EnumRequisitionStatus, StatusTimelineAccent>> = {
    [EnumRequisitionStatus.Open]: 'default',
    [EnumRequisitionStatus.UnderReview]: 'warning',
    [EnumRequisitionStatus.InProgress]: 'info',
};

function stageNote(stage: EnumRequisitionStatus, operatorName: string): string | undefined {
    switch (stage) {
        case EnumRequisitionStatus.Open:
            return `Ihre Bestellung ist bei ${operatorName} eingegangen.`;
        case EnumRequisitionStatus.UnderReview:
            return `${operatorName} prüft Ihre Bestellung.`;
        case EnumRequisitionStatus.InProgress:
            return `${operatorName} hat die Bestellung angenommen und führt sie aus.`;
        default:
            return undefined;
    }
}

export interface PortalRequisitionTimelineInput {
    status: EnumRequisitionStatus;

    /** When the customer submitted the order. */
    submittedAt?: string;

    /**
     * When the requisition last changed status. The backend writes the requisition only when its
     * derived status actually moves, so for an order that has come to rest this is the moment it
     * became "Abgeschlossen" or "Abgelehnt". Both terminal stages are dated from it: the orders keep
     * being touched afterwards (invoicing, documents), so their own last change would let the date
     * drift away from the day the order actually came to rest.
     */
    statusChangedAt?: string;

    operatorName: string;

    /** Where the order came from. Defaults to a self-submitted one when the backend sends none. */
    origin?: EnumRequisitionOrigin;

    /** What the linked order snapshots reveal about the requisition, dates and rejection reason alike. */
    orderFacts?: PortalRequisitionOrderFacts;

    /** Shown on the completed step */
    completedNote?: ReactNode;
}

/** Derives the "Was passiert mit meiner Bestellung?" timeline from the current status. */
// @ai-generated 2026-08-12T07:36:46+00:00 by matthias.gessinger@synqony.com using Claude Opus 5
export function buildPortalRequisitionTimeline({
    status,
    submittedAt,
    statusChangedAt,
    operatorName,
    origin,
    orderFacts,
    completedNote,
}: PortalRequisitionTimelineInput): StatusTimelineStep[] {
    const facts = orderFacts ?? {};
    const submission = describeSubmission(origin, operatorName, submittedAt);

    if (status === EnumRequisitionStatus.Rejected) {
        return [
            {
                key: 'submitted',
                title: submission.title,
                subtitle: formatStageDate(submittedAt, 'done', 'datetime'),
                state: 'done',
                note: submission.note,
            },
            {
                key: 'review',
                title: getPortalRequisitionStatusLabel(EnumRequisitionStatus.UnderReview),
                subtitle: formatStageDate(facts.reviewedAt, 'done'),
                state: 'done',
            },
            {
                key: 'rejected',
                title: getPortalRequisitionStatusLabel(EnumRequisitionStatus.Rejected),
                subtitle: formatStageDate(statusChangedAt, 'done'),
                state: 'failed',
                note:
                    facts.rejectionReason ??
                    `${operatorName} konnte die Bestellung nicht annehmen. Bitte nehmen Sie für Rückfragen Kontakt auf.`,
            },
        ];
    }

    const currentIndex = TIMELINE_STAGES.indexOf(status);

    return TIMELINE_STAGES.map((stage, index) => {
        const state = stageState(index, currentIndex);
        const isCompletion = stage === EnumRequisitionStatus.Completed;
        const isSubmission = stage === EnumRequisitionStatus.Open;

        return {
            key: EnumRequisitionStatus[stage],
            title: isSubmission ? submission.title : getPortalRequisitionStatusLabel(stage),
            subtitle: stageSubtitle(stage, state, submittedAt, statusChangedAt, facts),
            state,
            accent: STAGE_ACCENTS[stage],
            note:
                isCompletion && state === 'done'
                    ? completedNote
                    : noteForOpenStage(state, stage, operatorName, submission.note),
        };
    });
}

/**
 * How the first step reads. An order the operator took down is not something the customer submitted,
 * so the step names the act of recording it and says so for as long as the order exists — the customer
 * would otherwise have no way of telling their own orders from the ones entered for them.
 */
// @ai-generated 2026-08-12T07:36:46+00:00 by matthias.gessinger@synqony.com using Claude Opus 5
function describeSubmission(
    origin: EnumRequisitionOrigin | undefined,
    operatorName: string,
    submittedAt: string | undefined,
): { title: string; note?: string } {
    if (isSelfSubmittedRequisition(origin)) {
        return { title: getPortalRequisitionStatusLabel(EnumRequisitionStatus.Open) };
    }

    const on = submittedAt ? ` am ${renderDateTime(submittedAt, 'date')}` : '';
    return {
        title: 'Auftrag aufgenommen',
        note: `${operatorName} hat den Auftrag${on} für Sie erfasst.`,
    };
}

function noteForOpenStage(
    state: StatusTimelineStep['state'],
    stage: EnumRequisitionStatus,
    operatorName: string,
    submissionNote: string | undefined,
): string | undefined {
    if (stage === EnumRequisitionStatus.Open && submissionNote) {
        return submissionNote;
    }
    return state === 'current' ? stageNote(stage, operatorName) : undefined;
}

/**
 * The date a stage carries. Only a stage that has actually been reached may carry one — the order
 * snapshots know dates that run ahead of the customer's status (the operator's completion date is
 * already set once execution finishes, long before the order is done).
 * @ai-generated 2026-08-27T11:14:57+00:00 by matthias.gessinger@synqony.com using Claude Opus 5
 */
function stageSubtitle(
    stage: EnumRequisitionStatus,
    state: StatusTimelineStep['state'],
    submittedAt: string | undefined,
    statusChangedAt: string | undefined,
    facts: PortalRequisitionOrderFacts,
): string | undefined {
    switch (stage) {
        case EnumRequisitionStatus.Open:
            return submittedAt ? renderDateTime(submittedAt, 'datetime') : subtitleFor(state);
        case EnumRequisitionStatus.UnderReview:
            return formatStageDate(facts.reviewedAt, state);
        // Nothing records when the order left the staging gate, and its last change is not a start, so
        // the stage is dated by its end instead: the last order to finish execution. A stage still
        // running stays undated rather than claiming a beginning that moves with every edit.
        case EnumRequisitionStatus.InProgress:
            return state === 'done' ? formatStageDate(facts.executionFinishedAt, state) : subtitleFor(state);
        case EnumRequisitionStatus.Completed:
            return state === 'done' ? formatStageDate(statusChangedAt, state) : subtitleFor(state);
        default:
            return subtitleFor(state);
    }
}

/** "seit <date>" while a stage is running, a plain date once it is behind us.
 * @ai-generated 2026-08-10T09:15:19+00:00 by niklas.eicker@tngtech.com using Claude Opus 5
 */
function formatStageDate(
    date: string | undefined,
    state: StatusTimelineStep['state'],
    format: 'date' | 'datetime' = 'date',
): string | undefined {
    if (!date) {
        return subtitleFor(state);
    }

    const rendered = renderDateTime(date, format);
    return state === 'current' ? `seit ${rendered}` : rendered;
}

function stageState(index: number, currentIndex: number): StatusTimelineStep['state'] {
    // An unknown status must not claim progress.
    if (currentIndex < 0) {
        return 'pending';
    }
    if (index < currentIndex) {
        return 'done';
    }
    if (index > currentIndex) {
        return 'pending';
    }
    // Reaching the last stage completes it, so a finished order does not keep pulsing.
    return index === TIMELINE_STAGES.length - 1 ? 'done' : 'current';
}

function subtitleFor(state: StatusTimelineStep['state']): string | undefined {
    return state === 'pending' ? PENDING_SUBTITLE : undefined;
}
